import SectionTitle from '@components/home/sectionTitle';
import styled from '@emotion/styled';
import { ModuleFragment } from '@graphql/generated/graphql';
import { useContentScroller } from '@hooks';
import { Space } from '@lib/uikit/theme/types';
import { OrderedModule } from '@types';
import localeDirection from '@utils/locale/direction';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import {
    CSSProperties,
    Children,
    FC,
    PropsWithChildren,
    ReactElement,
    RefObject,
    cloneElement,
    isValidElement,
} from 'react';
import { isMobile } from 'react-device-detect';

const HeaderNavigation = dynamic(
    () => import('./ContentScrollerHeaderNavigation'),
    { ssr: false }
);

const BodyNavigationButton = dynamic(
    () => import('./ContentScrollerBodyNavigationButton'),
    { ssr: false }
);

interface Props extends PropsWithChildren {
    navigationPlacement?: 'header' | 'body';
    paddingInline?: Space;
    listItemWidth?: CSSProperties['width'];
    module?: OrderedModule | ModuleFragment;
    scrollOptions?: ScrollIntoViewOptions;
}

interface Child extends ReactElement {
    parentRef: RefObject<HTMLUListElement>;
}

const Header = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-block-start: ${({ theme }) => theme.spacing.md};
    margin-block-end: ${({ theme }) => theme.spacing.xs};
    margin-inline-end: ${({ theme }) => theme.spacing.sm};
    align-items: center;
`;

const Title = styled(SectionTitle)<{ paddingInline: Space }>`
    padding-inline-start: ${({ theme, paddingInline }) =>
        theme.spacing[paddingInline]};
`;

const ScrollableListContainer = styled.div`
    position: relative;
    width: 100%;
`;

const ScrollableList = styled.ul<{ paddingInline: Space }>`
    pointer-events: auto;
    display: -webkit-box;
    overflow-x: scroll;
    overflow-y: hidden;
    padding-inline: ${({ theme, paddingInline }) =>
        theme.spacing[paddingInline]};
    li:last-child {
        margin-inline-end: 0;
    }
`;

const ListElement = styled.li<{ width?: CSSProperties['width'] }>`
    list-style: none;
    width: ${({ width }) => width};
    margin-inline-end: ${({ theme }) => theme.spacing.sm};
`;

const ContentScroller: FC<Props> = ({
    children,
    navigationPlacement = 'header',
    paddingInline = 'md',
    listItemWidth,
    module,
    scrollOptions,
}) => {
    const { locale } = useRouter();
    const isRtl = localeDirection(locale) === 'rtl';
    const {
        parentRef,
        childrenRefs,
        allowBack,
        allowForth,
        handleBack,
        handleForth,
    } = useContentScroller({ reverse: isRtl, module, scrollOptions });
    const title = module?.Module?.Title;

    return (
        <>
            {(title || navigationPlacement === 'header') && (
                <Header>
                    {title && (
                        <Title paddingInline={paddingInline}>{title}</Title>
                    )}
                    {navigationPlacement === 'header' && !isMobile && (
                        <HeaderNavigation
                            onBack={handleBack}
                            onForth={handleForth}
                            allowBack={allowBack}
                            allowForth={allowForth}
                        />
                    )}
                </Header>
            )}
            <ScrollableListContainer>
                {navigationPlacement === 'body' && allowBack && !isMobile && (
                    <BodyNavigationButton
                        move='back'
                        distance={paddingInline}
                        onClick={handleBack}
                    />
                )}
                <ScrollableList ref={parentRef} paddingInline={paddingInline}>
                    {Children.toArray(children)
                        .filter(isValidElement)
                        .map((child, index) => (
                            <ListElement
                                key={index}
                                width={listItemWidth}
                                ref={(ref) => {
                                    childrenRefs.current[index] = ref!;
                                }}
                            >
                                {cloneElement<Child>(child as Child, {
                                    parentRef,
                                })}
                            </ListElement>
                        ))}
                </ScrollableList>
                {navigationPlacement === 'body' && allowForth && !isMobile && (
                    <BodyNavigationButton
                        move='forth'
                        distance={paddingInline}
                        onClick={handleForth}
                    />
                )}
            </ScrollableListContainer>
        </>
    );
};

export default ContentScroller;
